import { useState, useMemo, useCallback } from "react";
import { groupBy, some } from "lodash";
import { useSelector } from "react-redux";
import { orderBy } from "lodash";
import { strings } from "../i18n";
import { enums, helpers, format } from "../lib";

/**
 * Use Audit Summary Data Hook
 */
export default (type) => {
    const altAssets = useSelector((state) => state.altAssets);
    const assetAuditors = useSelector((state) => state.assetAuditors);
    const assets = useSelector((state) => state.assets);
    const auditAssets = useSelector((state) => state.auditAssets);
    const auditControllers = useSelector((state) => state.auditControllers);
    const typesList = useSelector((state) => state.typesList);

    const [data, setData] = useState([]);
    const [csvData, setCsvData] = useState([]);
    const [loadData, setLoadData] = useState(true);

    const pluralizeImage = (count) => {
        if (count <= 0) return ""; // skip if no images
        return count === 1 ? "1 image" : `${count} images`;
    };

    const pluralize = (count, noun) =>
        count === 1 ? `1 ${noun}` : `${count} ${noun}s`;

    const getAction = useCallback(
        (value) => {
            const {
                isAssigned,
                excluded,
                controllerExcluded,
                auditAssetAuditors = [],
                controllerAssetAuditors = []
            } = value;

            // Helper to build action string
            const buildAction = ({
                role,
                actionVerb,
                excludedVerb,
                auditors,
                isExcluded
            }) => {
                const imagesCount = auditors.reduce(
                    (sum, a) => sum + a.auditImages.length,
                    0
                );
                const documentsCount = auditors.reduce(
                    (sum, a) =>
                        sum + (a.auditDocuments ? a.auditDocuments.length : 0),
                    0
                );

                if (imagesCount <= 0 && documentsCount <= 0) return "";

                const noteAdded = auditors.some(
                    (a) => a.auditNotes && a.auditNotes.length > 0
                );

                // Images and documents are reported separately: images use
                // the role's own verb (taken/uploaded/attached), documents
                // are always "added".
                const imageVerb = isExcluded ? excludedVerb : actionVerb;
                const parts = [];
                if (imagesCount > 0) {
                    parts.push(
                        `${imageVerb} ${pluralize(imagesCount, "image")}`
                    );
                }
                if (documentsCount > 0) {
                    parts.push(`added ${pluralize(documentsCount, "document")}`);
                }
                const combined = parts.join(" and ");

                if (isExcluded) {
                    return noteAdded
                        ? `${role} excluded the asset and ${combined} with a note`
                        : `${role} excluded the asset`;
                }

                return noteAdded
                    ? `${role} ${combined} and added a note`
                    : `${role} ${combined}`;
            };

            /** ---------- CONTROLLER ---------- */
            if (controllerAssetAuditors.length > 0) {
                return buildAction({
                    role: "controller",
                    actionVerb: "uploaded",
                    excludedVerb: "uploaded",
                    auditors: controllerAssetAuditors,
                    isExcluded: controllerExcluded
                });
            }

            /** ---------- AUDITOR ---------- */
            if (isAssigned && auditAssetAuditors.length > 0) {
                return buildAction({
                    role: "auditor has",
                    actionVerb: "taken",
                    excludedVerb: "attached",
                    auditors: auditAssetAuditors,
                    isExcluded: excluded
                });
            }

            /** ---------- FALLBACK ---------- */
            if (value.isResolved) {
                const { length } = value.altAssetResolutions;
                const last = value.altAssetResolutions[length - 1];
                return strings(last && last.isMatched ? "matched" : "created");
            }

            return "";
        },
        [typesList]
    );

    const getCore = useCallback(
        (value) => {
            const typeId = 1;
            const { typeIdentifiers } = typesList;
            const identiferIds = typeIdentifiers.data
                .filter((item) => item.typeId === typeId)
                .map((item) => item.identifierId);

            const res = typeIdentifiers.data
                .filter(
                    (item) =>
                        identiferIds.includes(item.identifierId) &&
                        item.typeId === typeId
                )
                .reduce(
                    (aggregate, item) => ({
                        ...aggregate,
                        [item.value]: value[`identifier${item.identifierId}`]
                            ? value[`identifier${item.identifierId}`]
                            : ""
                    }),
                    {}
                );
            const make = helpers.getListItemForId(typesList.makes, value.make);
            const type = helpers.getListItemForId(
                typesList.assetTypes,
                value.type
            );
            return {
                ...res,
                identifier1: value.identifier1 || "",
                identifier2: value.identifier2 || "",
                identifier3: value.identifier3 || "",
                identifier4: value.identifier4 || "",
                make: make.name,
                type: type.name
            };
        },
        [typesList]
    );

    const getYearModel = useCallback(
        (value) => {
            const yearModel = ["year", "model"];
            const attributes = [];
            typesList.attributes.data.map((item) =>
                yearModel.includes(item.name) ? attributes.push(item) : false
            );

            const result = attributes.reduce((acc, item) => {
                const match = value?.attributes
                    ? value?.attributes.find(
                          (entry) => entry.attribute === item.id
                      )
                    : null;
                acc[item.name] = match ? match.value : "";
                return acc;
            }, {});
            return result;
        },
        [typesList]
    );

    /** Map services with the name with the typeslist  */
    const monetaryServicesCols = useMemo(() => {
        const monetaryServicesTypes = [];
        typesList.monetaryValueTypes.data.map((item) =>
            monetaryServicesTypes.push({ id: item.name })
        );
        return monetaryServicesTypes;
    }, [typesList.monetaryValueTypes]);

    const getStatus = useCallback((value) => {
        if (value.isApproved) {
            return strings("approved");
        }
        if (value.isApproved === false) {
            if (value.reissuedStatus === enums.status.reissued.reissued) {
                return strings("reissued");
            }
            if (value.reissuedStatus === enums.status.reissued.resolved) {
                return strings("resolved");
            }
            if (value.reissuedStatus === enums.status.reissued.unresolved) {
                return strings("unresolved");
            }
            return strings("rejected");
        }
        if (value.isResolved) {
            const { length } = value.altAssetResolutions;
            const last = value.altAssetResolutions[length - 1];
            return strings(last && last.isMatched ? "matched" : "created");
        }
        if (value.isAssigned && value.isApproved === null) {
            return strings("pending");
        }
        return "";
    }, []);

    const byAuditAssetId = useMemo(
        () => groupBy(assetAuditors.data, "auditAsset"),
        [assetAuditors]
    );

    /** Audit assets note */
    const getAuditAssetAuditorsNotes = useCallback((value) => {
        /** Consolidated notes */
        const notes = value.reduce((aggregate, item) => {
            if (item.auditNotes) {
                aggregate.push(item.auditNotes);
            }
            return aggregate;
        }, []);

        const note = notes.map((item) =>
            item.length > 0 ? item[0].content : ""
        );
        return note;
    });

    /** Alt assets note */
    const getAltAssetsNotes = useCallback((value) => {
        const content = orderBy(value, ["createdDate"], ["desc"]);
        return content.length > 0 ? content[0].note : "";
    });

    /** Get current Asset  */
    const getCurrentControllerAsset = (auditAssetId) =>
        auditControllers.data.filter(
            (item) => item.auditAsset === auditAssetId
        );

    /** Get controller notes
     * 1. Notes can be from different controllers
     * 2. Sort based on the latest note
     */
    const getControllerNotes = useCallback((value) => {
        const notes = value
            .reduce((aggregate, item) => {
                if (item.auditNotes) {
                    aggregate.push(item.auditNotes);
                }
                return aggregate;
            }, [])
            .flat()
            .sort(
                (v1, v2) => new Date(v2.createdDate) - new Date(v1.createdDate)
            );
        return notes && notes.length > 0 ? notes[0].content : "";
    });
    /** Check if the asset is excluded form
     * An asset can have more than one auditor/controller record (e.g. an
     * upload happens before an exclusion is recorded as a separate row), so
     * this has to be "any record carries an exclusion reason", matching how
     * V3-API itself treats exclusion - not "every record does", which
     * silently drops the exclusion as soon as a second, non-excluded record
     * exists for the same asset.
     */
    const getExclude = (auditor) => {
        return (
            Boolean(auditor.length) &&
            some(auditor, (a) => Boolean(a.exclusionReason))
        );
    };
    /** Get exclusion Reason id 
    TODO:
        1. When exclusion reason array is sent from backed replace 
        [...] with aggregate.push()
    */
    const getExclusionReasonId = (auditor) => {
        return auditor.reduce(
            (aggregate, a) =>
                a.exclusionReason
                    ? [...aggregate, a.exclusionReason]
                    : aggregate,
            []
        );
    };

    /** Check if this is soldDate  */
    const isSoldDate = (exclusionReasonName) =>
        exclusionReasonName.includes("asset is sold") ? true : false;

    /** Get ExclusionReason Name */
    const getExclusionReasonName = (
        exclusionReasons,
        isAssigned,
        excluded,
        data
    ) => {
        if (isAssigned && excluded) {
            const exclusionReason = exclusionReasons.map((id) => {
                const { name } = helpers.getListItemForId(
                    typesList.exclusionReasonTypes,
                    id
                );
                return name;
            });
            /** if sold date is present and if the type is csv */
            let date = "";

            if (
                isSoldDate(exclusionReason) &&
                data.soldDate &&
                type === "csv"
            ) {
                date = `: ${format.dateTimeNew(data.soldDate)}  `;
            }
            return `${strings("excluded")}: ${exclusionReason.join(
                ", "
            )}${date}`;
        }
        return "";
    };

    const formatCsvData = (data) => {
        return data.map((item) => {
            // Create a shallow copy of the object
            const newItem = {};

            // Modify boolean values and capitalize each word in the keys
            for (const [key, value] of Object.entries(item)) {
                // Capitalize the first letter of each word in the key
                const capitalizedKey = key
                    .replace(/([a-z])([A-Z])/g, "$1 $2")
                    .split(" ")
                    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                    .join(" ");
                // Modify boolean values
                newItem[capitalizedKey] =
                    typeof value === "boolean" ? (value ? "Yes" : "No") : value;
            }

            return newItem;
        });
    };

    const next = useMemo(() => {
        setLoadData(true);
        const res = [
            ...auditAssets.data.map((item) => {
                const asset = helpers.getListItemForId(assets, item.asset);
                const auditAssetAuditors = byAuditAssetId[item.id] || [];
                const controllerAssetAuditors = getCurrentControllerAsset(
                    item.id
                );
                const assetStatus = helpers.getListItemForId(
                    typesList.assetStatus,
                    item.status
                );
                const auditorExcluded = getExclude(auditAssetAuditors);
                const auditorExclusionReasonId =
                    getExclusionReasonId(auditAssetAuditors);
                const auditorExclusionReason = getExclusionReasonName(
                    auditorExclusionReasonId,
                    item.isAssigned,
                    auditorExcluded,
                    auditAssetAuditors[0]
                );
                const controllerExcluded = getExclude(controllerAssetAuditors);
                const controllerExclusionReasonId = getExclusionReasonId(
                    controllerAssetAuditors
                );
                const controllerExclusionReason = getExclusionReasonName(
                    controllerExclusionReasonId,
                    item.isAssigned,
                    controllerExcluded,
                    controllerAssetAuditors[0]
                );
                const reviewStatus = getStatus(item);

                const excluded = getExclude(auditAssetAuditors);
                /** Exclusion Reasa */
                const exclusionReasons = auditAssetAuditors.reduce(
                    (aggregate, a) =>
                        a.exclusionReason
                            ? [...aggregate, a.exclusionReason]
                            : aggregate,
                    []
                );

                return {
                    ...getCore(asset),
                    ...getYearModel(asset),
                    action: getAction({
                        ...item,
                        auditAssetAuditors,
                        excluded,
                        exclusionReasons,
                        controllerExcluded,
                        controllerExclusionReason,
                        controllerAssetAuditors
                    }),
                    excluded: "",
                    assetStatus: assetStatus
                        ? strings(`${assetStatus.name}`)
                        : "",
                    exclusionReasons: "",
                    auditorExcluded,
                    auditorExclusionReason,
                    auditorNotes:
                        getAuditAssetAuditorsNotes(auditAssetAuditors),
                    controllerExcluded,
                    controllerExclusionReason,
                    controllerNotes: getControllerNotes(
                        controllerAssetAuditors
                    ),
                    assigned: item.isAssigned === true,
                    approved: item.isApproved === true,
                    expected: item.isExpected && item.isMandatory,
                    mandatory: item.isMandatory === true,
                    pending:
                        item.isApproved === null && item.isAssigned === true,
                    rejected: item.isApproved === false,
                    resolved: false,
                    seen: some(
                        auditAssetAuditors,
                        (a) => !Boolean(a.exclusionReason)
                    ),
                    reviewStatus: reviewStatus ? reviewStatus : "",
                    unexpected: false,
                    sharedValidationAsset: item.sharedValidationAsset
                };
            }),
            ...altAssets.data.map((item) => {
                return {
                    ...getCore(item),
                    action: getAction(item),
                    approved: false,
                    assigned: true,
                    excluded: false,
                    exclusionReasons: [],
                    auditorExcluded: "",
                    auditorExclusionReason: "",
                    auditorNotes: getAltAssetsNotes(item.notes),
                    controllerExcluded: "",
                    controllerExclusionReason: "",
                    controllerNotes: "",
                    expected: false,
                    mandatory: false,
                    pending: item.isResolved === false,
                    rejected: false,
                    resolved: item.isResolved === true,
                    reviewStatus:
                        item.isResolved === true
                            ? strings("resolved")
                            : strings("pending"),
                    assetStatus: "",
                    seen: true,
                    year: "",
                    model: "",
                    unexpected: true
                };
            })
        ];

        setData(res);
        setCsvData(formatCsvData(res));
        setLoadData(false);
        return res;
    }, [
        altAssets,
        auditControllers,
        assets,
        auditAssets,
        byAuditAssetId,
        typesList
    ]);

    return { loadData, data, csvData };
};

//TODO:
//1. One hook for the csv  download and the table should be used
//2. Based on the view the data should be taken from the hook
